import { useState, useCallback, useEffect } from "react";
import { HeroCarousel } from "./HeroCarousel";
import { CategoryCard } from "./CategoryCard";
// No static mocks needed anymore
import {
  Image as ImageIcon,
  Video,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { IMAGE, VIDEO } from "../../utilities/constants/MediaType";
import { motion, AnimatePresence } from "framer-motion";
import { getAllGalleryItems } from "./GalleryService";
import { EntitiesListResponseModel } from "../../utilities/models/EntitiesListResponseModel";
import { Link } from "react-router-dom";
import { CONTACT_PAGE_URL } from "../../utilities/constants/PagesEndpoints";

const GalleryPage = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [galleryItems, setGalleryItems] = useState(EntitiesListResponseModel);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getAllGalleryItems(
      (data) => {
        setGalleryItems(data);
        setIsLoading(false);
      },
      { sort: "id,desc" },
    );
  }, []);

  // Transform backend items into categories if they have the same title,
  // or treat each as a separate item.
  const categoriesFromBackend = galleryItems.content.reduce((acc, item) => {
    // Skip if sharedMedia is missing or malformed to prevent crashes
    if (!item.sharedMedia?.mediaUrl) return acc;

    // Use 'category' for grouping, fallback to 'Uncategorized' if null
    const categoryName = item.category || "Uncategorized";

    const existing = acc.find((c) => c.name === categoryName);
    if (existing) {
      existing.images.push(item.sharedMedia.mediaUrl);
      existing.count += 1;
    } else {
      acc.push({
        id: item.id.toString(),
        name: categoryName,
        images: [item.sharedMedia.mediaUrl],
        count: 1,
        type: item.sharedMedia.type || IMAGE,
        description: item.description,
      });
    }
    return acc;
  }, []);

  const carouselItems = galleryItems.content
    .filter((item) => item.sharedMedia?.mediaUrl)
    .slice(0, 5)
    .map((item) => ({
      id: item.id,
      image: item.sharedMedia.mediaUrl,
      title: item.title,
      type: item.sharedMedia.type || IMAGE,
    }));

  const filteredCategories =
    activeTab === "all"
      ? categoriesFromBackend
      : activeTab === "pictures"
        ? categoriesFromBackend.filter(
            (c) => c.type && c.type.startsWith(IMAGE),
          )
        : categoriesFromBackend.filter(
            (c) => c.type && c.type.startsWith(VIDEO),
          );

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setLightboxIndex(0);
  };

  const closeLightbox = () => {
    setSelectedCategory(null);
    setLightboxIndex(0);
  };

  const nextImage = useCallback(() => {
    if (selectedCategory) {
      setLightboxIndex((prev) => (prev + 1) % selectedCategory.images.length);
    }
  }, [selectedCategory]);

  const prevImage = useCallback(() => {
    if (selectedCategory) {
      setLightboxIndex(
        (prev) =>
          (prev - 1 + selectedCategory.images.length) %
          selectedCategory.images.length,
      );
    }
  }, [selectedCategory]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedCategory) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };

    globalThis.addEventListener("keydown", handleKeyDown);
    return () => globalThis.removeEventListener("keydown", handleKeyDown);
  }, [selectedCategory, nextImage, prevImage]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-200 font-sans selection:bg-indigo-500 selection:text-white pb-20 transition-colors duration-300">
      {/* Lightbox Overlay */}
      <AnimatePresence>
        {selectedCategory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col"
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 sm:p-6 text-white z-50">
              <div className="text-left">
                <h3 className="text-xl font-bold">{selectedCategory.name}</h3>
                <p className="text-sm text-white/60">
                  {lightboxIndex + 1} / {selectedCategory.images.length}
                </p>
              </div>
              <button
                onClick={closeLightbox}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <X size={28} />
              </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 relative flex items-center justify-center p-4">
              {/* Navigation Buttons */}
              <button
                onClick={prevImage}
                className="absolute left-4 z-50 p-3 rounded-full bg-black/20 hover:bg-black/50 text-white backdrop-blur-md transition-all hover:scale-110 hidden sm:block"
              >
                <ChevronLeft size={32} />
              </button>

              <button
                onClick={nextImage}
                className="absolute right-4 z-50 p-3 rounded-full bg-black/20 hover:bg-black/50 text-white backdrop-blur-md transition-all hover:scale-110 hidden sm:block"
              >
                <ChevronRight size={32} />
              </button>

              {/* Image */}
              <motion.div
                key={lightboxIndex}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="max-h-full max-w-full relative py-2"
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={(e, { offset, velocity }) => {
                  const swipe = Math.abs(offset.x) * velocity.x;
                  if (swipe < -100) nextImage();
                  else if (swipe > 100) prevImage();
                }}
              >
                <img
                  src={selectedCategory.images[lightboxIndex]}
                  alt={`Gallery ${lightboxIndex}`}
                  className="max-h-[85vh] w-auto max-w-full rounded-lg shadow-2xl object-contain mx-auto"
                />
              </motion.div>
            </div>

            {/* Thumbnails (Optional, can be added later) */}
            <div className="h-16 justify-center gap-2 mb-6 hidden md:flex px-4">
              {selectedCategory.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className={`relative w-20 h-full rounded-md overflow-hidden transition-all duration-300 ${lightboxIndex === idx ? "ring-2 ring-white scale-110 z-10" : "opacity-50 hover:opacity-100"}`}
                >
                  <img
                    src={img}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="pt-24 px-4 md:px-8 max-w-7xl mx-auto space-y-16">
        {/* Section 1: Hero Gallery Carousel */}
        {carouselItems.length > 0 && (
          <section>
            <div className="text-center mb-6">
              <h1 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-2 tracking-tight">
                Featured Shots
              </h1>
              <p className="text-slate-500 dark:text-slate-400 max-w-lg mx-auto">
                Curated selection of the finest imagery and motion from our
                latest events.
              </p>
            </div>
            <HeroCarousel items={carouselItems} />
          </section>
        )}

        {/* Section 2: Categories */}
        <section className="space-y-8">
          {/* Header & Tabs */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-slate-200 dark:border-white/10 pb-4">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-1">
                Gallery
              </h2>
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                Explore our collection of memorable moments
              </p>
            </div>

            {/* Custom Tab Switcher */}
            <div className="flex items-center gap-2 p-1 bg-slate-200 dark:bg-slate-900/50 rounded-xl border border-slate-300 dark:border-white/5">
              <button
                onClick={() => setActiveTab("all")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${activeTab === "all" ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-lg" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white"}`}
              >
                All
              </button>
              <button
                onClick={() => setActiveTab("pictures")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${activeTab === "pictures" ? "bg-indigo-600 text-white shadow-lg shadow-indigo-900/50" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white"}`}
              >
                <ImageIcon className="w-4 h-4" />
                Pictures
              </button>
              <button
                onClick={() => setActiveTab("videos")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${activeTab === "videos" ? "bg-indigo-600 text-white shadow-lg shadow-indigo-900/50" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white"}`}
              >
                <Video className="w-4 h-4" />
                Videos
              </button>
            </div>
          </div>

          {/* Grid Layout */}
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 py-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-[4/3] w-full bg-slate-200 dark:bg-slate-800 rounded-2xl animate-pulse" />
                  <div className="h-6 w-1/2 bg-slate-200 dark:bg-slate-800 rounded animate-pulse mx-auto" />
                </div>
              ))}
            </div>
          ) : filteredCategories.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 py-8">
              {filteredCategories.map((category) => (
                <CategoryCard
                  key={category.id}
                  category={category}
                  onClick={handleCategoryClick}
                />
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center py-24 px-4 text-center"
            >
              <div className="w-24 h-24 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-8 rotate-3 group-hover:rotate-0 transition-transform duration-500 shadow-inner">
                <ImageIcon className="w-10 h-10 text-slate-400 dark:text-slate-500" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                A Canvas Awaiting Memories
              </h3>
              <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-10 leading-relaxed font-light">
                We're currently curating our latest batch of breathtaking
                moments. Our gallery is being refreshed with new stories of
                celebration and elegance.
              </p>
              <Link
                to={CONTACT_PAGE_URL}
                className="inline-flex items-center gap-2 text-indigo-600 dark:text-indigo-400 font-semibold hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors group"
              >
                Connect with us for a tour
                <ChevronRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </motion.div>
          )}
        </section>
      </main>
    </div>
  );
};

export default GalleryPage;
